import { useEffect, useState } from "react";
import api from "../../lib/api";
import { PageHeader, Loading, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ScrollText, Table as TableIcon, Rocket, Save, PlusCircle } from "lucide-react";
import { toast } from "sonner";

function StatusBadge({ value }) {
  const tone = value === "ACTIVE" ? "default" : value === "DRAFT" ? "secondary" : "outline";
  return <Badge data-testid={`legal-status-${value}`} variant={tone}>{value || "—"}</Badge>;
}

function AgreementsPanel() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ version: "", summary_md: "", text_md: "", title: "", watermark: "Draft — pending legal review" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/legal/agreements").then((r) => setRows(r.data.agreements || []));
  useEffect(() => { load(); }, []);

  const openEdit = async (id) => {
    const response = await api.get(`/admin/legal/agreements/${id}`);
    setEditing(response.data);
  };

  const save = async () => {
    try {
      await api.put(`/admin/legal/agreements/${editing.agreement_id}`, { text_md: editing.text_md, summary_md: editing.summary_md, title: editing.title, watermark: editing.watermark });
      toast.success("Agreement draft saved");
      setEditing(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save");
    }
  };

  const publish = async (id) => {
    if (!window.confirm("Publish this agreement as ACTIVE? Any current ACTIVE version will be archived.")) return;
    try {
      await api.post(`/admin/legal/agreements/${id}/publish`, {});
      toast.success("Agreement published");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not publish");
    }
  };

  const create = async () => {
    try {
      await api.post("/admin/legal/agreements", draft);
      toast.success("Draft agreement created");
      setCreating(false);
      setDraft({ version: "", summary_md: "", text_md: "", title: "", watermark: "Draft — pending legal review" });
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create");
    }
  };

  if (rows === null) return <Loading />;

  return (
    <div className="space-y-4" data-testid="legal-agreements-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <ScrollText className="h-5 w-5" /> Agreements
        </h3>
        <Button data-testid="legal-agreement-new" size="sm" onClick={() => setCreating(true)}>
          <PlusCircle className="h-4 w-4" /> New draft
        </Button>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No agreements yet" />
      ) : (
        <table className="w-full border text-sm" data-testid="legal-agreements-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-2 text-left">Version</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Applicable to</th>
              <th className="p-2 text-left">Published</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.agreement_id} className="border-t" data-testid={`legal-agreement-row-${row.version}`}>
                <td className="p-2 font-mono">{row.version}</td>
                <td className="p-2"><StatusBadge value={row.status} /></td>
                <td className="p-2">{(row.applicable_to || []).join(", ")}</td>
                <td className="p-2">{row.published_at || "—"}</td>
                <td className="p-2 flex gap-2">
                  <Button variant="outline" size="sm" data-testid={`legal-agreement-edit-${row.version}`} onClick={() => openEdit(row.agreement_id)}>Open</Button>
                  {row.status === "DRAFT" && (
                    <Button size="sm" data-testid={`legal-agreement-publish-${row.version}`} onClick={() => publish(row.agreement_id)}>
                      <Rocket className="h-4 w-4" /> Publish
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Agreement v{editing?.version} · {editing?.status}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" data-testid="legal-agreement-title" />
                <Input value={editing.watermark || ""} onChange={(e) => setEditing({ ...editing, watermark: e.target.value })} placeholder="Watermark" data-testid="legal-agreement-watermark" />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Plain-language summary (optional, max ~20 lines). Accompanies the full agreement; does not replace it.</label>
                <Textarea
                  data-testid="legal-agreement-summary"
                  className="h-40 font-mono text-xs"
                  value={editing.summary_md || ""}
                  onChange={(e) => setEditing({ ...editing, summary_md: e.target.value })}
                  disabled={editing.status !== "DRAFT"}
                  placeholder="# Plain-language summary (optional)"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 font-medium">Full agreement (binding)</label>
                <Textarea
                  data-testid="legal-agreement-md"
                  className="h-96 font-mono text-xs"
                  value={editing.text_md}
                  onChange={(e) => setEditing({ ...editing, text_md: e.target.value })}
                  disabled={editing.status !== "DRAFT"}
                />
              </div>
              <div className="flex gap-2 justify-end">
                {editing.status === "DRAFT" && (
                  <Button data-testid="legal-agreement-save" onClick={save}>
                    <Save className="h-4 w-4" /> Save draft
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>New agreement draft</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Version (e.g. 1.1)" value={draft.version} onChange={(e) => setDraft({ ...draft, version: e.target.value })} data-testid="legal-agreement-new-version" />
            <Input placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="legal-agreement-new-title" />
            <Textarea className="h-40 font-mono text-xs" placeholder="Plain-language summary (optional, accompanies the full agreement, does not replace it)" value={draft.summary_md} onChange={(e) => setDraft({ ...draft, summary_md: e.target.value })} data-testid="legal-agreement-new-summary" />
            <Textarea className="h-72 font-mono text-xs" placeholder="Full agreement markdown…" value={draft.text_md} onChange={(e) => setDraft({ ...draft, text_md: e.target.value })} data-testid="legal-agreement-new-md" />
            <Button onClick={create} data-testid="legal-agreement-new-save">Create draft</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PENDING = "PENDING_CATEGORY_TERMS";

function SchedulesPanel() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/legal/schedules").then((r) => setRows(r.data.schedules || []));
  useEffect(() => { load(); }, []);

  const openEdit = async (id) => {
    const response = await api.get(`/admin/legal/schedules/${id}`);
    setEditing(response.data);
  };

  const save = async () => {
    try {
      await api.put(`/admin/legal/schedules/${editing.schedule_id}`, { rows: editing.rows, title: editing.title, watermark: editing.watermark });
      toast.success("Schedule draft saved");
      setEditing(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save");
    }
  };

  const publish = async (id) => {
    if (!window.confirm("Publish this schedule as ACTIVE? Any current ACTIVE schedule will be archived.")) return;
    try {
      await api.post(`/admin/legal/schedules/${id}/publish`, {});
      toast.success("Schedule published");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not publish — resolve PENDING_CATEGORY_TERMS first");
    }
  };

  const updateCell = (idx, field, value) => {
    const next = { ...editing, rows: editing.rows.map((row, i) => (i === idx ? { ...row, [field]: value } : row)) };
    setEditing(next);
  };

  if (rows === null) return <Loading />;

  return (
    <div className="space-y-4" data-testid="legal-schedules-panel">
      <h3 className="font-display text-lg font-semibold flex items-center gap-2">
        <TableIcon className="h-5 w-5" /> Commercial Schedules (per-category)
      </h3>
      {rows.length === 0 ? (
        <EmptyState title="No schedules yet" />
      ) : (
        <table className="w-full border text-sm" data-testid="legal-schedules-table">
          <thead className="bg-slate-50">
            <tr>
              <th className="p-2 text-left">Version</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Pending terms</th>
              <th className="p-2 text-left">Rows</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.schedule_id} className="border-t" data-testid={`legal-schedule-row-${row.version}`}>
                <td className="p-2 font-mono">{row.version}</td>
                <td className="p-2"><StatusBadge value={row.status} /></td>
                <td className="p-2">
                  {row.has_pending_terms ? <Badge variant="destructive">PENDING</Badge> : <Badge>Complete</Badge>}
                </td>
                <td className="p-2">{row.rows?.length || 0}</td>
                <td className="p-2 flex gap-2">
                  <Button variant="outline" size="sm" data-testid={`legal-schedule-edit-${row.version}`} onClick={() => openEdit(row.schedule_id)}>Open</Button>
                  {row.status === "DRAFT" && !row.has_pending_terms && (
                    <Button size="sm" data-testid={`legal-schedule-publish-${row.version}`} onClick={() => publish(row.schedule_id)}>
                      <Rocket className="h-4 w-4" /> Publish
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader><DialogTitle>Schedule v{editing?.version} · {editing?.status}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="overflow-x-auto">
                <table className="w-full border text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="p-2">Category</th>
                      <th className="p-2">Commission %</th>
                      <th className="p-2">Visiting fee</th>
                      <th className="p-2">Cancellation</th>
                      <th className="p-2">Warranty</th>
                      <th className="p-2">Weekly payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(editing.rows || []).map((row, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2 w-56">{row.category_name}</td>
                        {["commission_percent", "visiting_fee", "cancellation_rules", "warranty_revisit", "weekly_payout_rule"].map((field) => (
                          <td key={field} className="p-2">
                            <Input
                              className={row[field] === PENDING ? "border-red-400" : ""}
                              data-testid={`legal-schedule-cell-${idx}-${field}`}
                              value={row[field] || ""}
                              disabled={editing.status !== "DRAFT"}
                              onChange={(e) => updateCell(idx, field, e.target.value)}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500">
                Cells with <code>PENDING_CATEGORY_TERMS</code> must be replaced before the schedule can be published. Commission applies to visiting + labour only.
              </p>
              {editing.status === "DRAFT" && (
                <div className="flex justify-end">
                  <Button data-testid="legal-schedule-save" onClick={save}>
                    <Save className="h-4 w-4" /> Save draft
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminVendorLegal() {
  const [tab, setTab] = useState("agreements");
  return (
    <div className="p-6 space-y-6" data-testid="admin-vendor-legal">
      <PageHeader
        title="Vendor & Technician Legal"
        description="Manage the service agreement and per-category commercial schedule versions. Only ACTIVE versions are served in production."
      />
      <div className="flex gap-2 border-b">
        <button
          data-testid="legal-tab-agreements"
          className={`px-4 py-2 text-sm ${tab === "agreements" ? "border-b-2 border-primary" : ""}`}
          onClick={() => setTab("agreements")}
        >Agreements</button>
        <button
          data-testid="legal-tab-schedules"
          className={`px-4 py-2 text-sm ${tab === "schedules" ? "border-b-2 border-primary" : ""}`}
          onClick={() => setTab("schedules")}
        >Commercial Schedules</button>
      </div>
      {tab === "agreements" ? <AgreementsPanel /> : <SchedulesPanel />}
    </div>
  );
}
